/**
 * <Bidi> / <Num> — inline bidirectional isolation for embedded LTR tokens.
 *
 * Renders a `<bdi>` (CSS `unicode-bidi: isolate`) so a Latin/numeric token —
 * a date ("2026-06-10"), a time range ("08:00 – 17:00"), a number
 * ("1,234.56"), a currency amount ("JOD 78.5"), an ID, an email — keeps its
 * own internal LTR character order WITHOUT disturbing the surrounding text's
 * bidi run.
 *
 * WHY THIS EXISTS (RTL alignment bug, codebase sweep): the old pattern put
 * `dir="ltr"` directly on the value's block/cell/chip to fix digit ORDER. But
 * `dir="ltr"` also forces `text-align` (and logical `text-start`/`text-end`)
 * to resolve against LTR, so in an Arabic (RTL) page the value hugged the LEFT
 * instead of the logical start (right). The fix decouples the two concerns:
 *   - ORDER  → this inline `<Bidi>`/`<Num>` (isolate + dir).
 *   - ALIGN  → stays on the PARENT block via logical utilities (text-start /
 *              text-end), which now resolve against the page direction.
 *
 * So this primitive is ALWAYS inline and MUST NOT be made block-level — the
 * parent owns alignment. Replace `<el dir="ltr">{value}</el>` with
 * `<el><Num>{value}</Num></el>` (drop the `dir` from the layout element).
 *
 * `<Num>` is the common case (force LTR for numbers/dates). Use `<Bidi
 * dir="auto">` for free-text whose direction should be detected from content
 * (e.g. a tenant name that may be Arabic or Latin inside the opposite shell).
 *
 * Testing Philosophy: SKIP-tier — a thin `<bdi>` wrapper; RTL behavior is
 * covered by the Playwright RTL visual sweep, not a unit test.
 */
import * as React from "react";
import { cn } from "../../utils";

export interface BidiProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Embedding direction. `"ltr"` (the `<Num>` default) forces LTR order for
   * numbers/dates/codes; `"auto"` detects from the first strong character for
   * free text of unknown direction; `"rtl"` for the rare reverse case.
   */
  dir?: "ltr" | "rtl" | "auto";
  children: React.ReactNode;
}

export const Bidi = React.forwardRef<HTMLElement, BidiProps>(function Bidi(
  { dir = "ltr", className, children, ...rest },
  ref,
) {
  return (
    <bdi ref={ref} dir={dir} className={cn(className)} {...rest}>
      {children}
    </bdi>
  );
});

/**
 * <Num> — the date/number/amount/ID case: an LTR-isolated inline token.
 * Equivalent to `<Bidi dir="ltr">`. Pass `font-mono` / `tabular-nums` etc.
 * via `className` exactly as the old `dir="ltr"` element did.
 */
export const Num = React.forwardRef<HTMLElement, Omit<BidiProps, "dir">>(function Num(props, ref) {
  return <Bidi ref={ref} dir="ltr" {...props} />;
});
